import '../index.less';

import classNames from 'classnames';
import dayjs from 'dayjs';
import { isEmpty } from 'lodash';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ReactSortable, Sortable } from 'react-sortablejs';
import { AddIcon, DeleteIcon } from 'tdesign-icons-react';
import { Button, Dialog, Dropdown, DropdownOption, Form, MessagePlugin, Textarea } from 'tdesign-react';

import { IFormItemProps } from '@/components/form/interface';
import { DragIcon, More } from '@/components/icon';

import styles from './form-array.module.less';
import ItemComp from './Item';


const { FormItem } = Form;

const random = () => `array_${dayjs().valueOf()}`;

const options = [
  {
    content: '解析',
    value: 'parse',
  },
  {
    content: '导出',
    value: 'export',
  },
  {
    content: '清空',
    value: 'clear',
  },
];

interface IFormArrayProps {
  fieldsConfig: IFormItemProps[];
  form: any;
  repeatConfig?: {
    [key: string]: IFormItemProps[]
  };
}

export const FormArrayComponent: React.FC<
{
  fieldname: string;
  config: any;
  getFieldDecorator: any;
  value: string[];
  onChange: (value: string[]) => void;
  drag: boolean;
  hasOperation: boolean;
  buttonSize: 'small' | 'normal',
  disabled?: boolean;
} & IFormArrayProps
> = (props) => {
  const {
    drag = false,
    value = [],
    onChange,
    buttonSize = 'normal',
    hasOperation = true,
    disabled = false,
  } = props;

  const defaultValue = [''];
  const [form] = Form.useForm();
  const ref = useRef<any[]>([]);

  const [compValue, setCompValue] = useState<any>(defaultValue);
  const [tempKeys, setTempKeys] = useState([defaultValue]?.map(() => random()));

  const [keys, setKeys] = useState([defaultValue]?.map(() => random()));

  const [changeKeyIndex, setChangeKeyIndex] = useState<number>(-1);

  const [visible, setVisible] = useState(false);

  const clickHandler = useCallback((dropdownItem: DropdownOption) => {
    if (dropdownItem.value === 'parse') {
      setVisible(true);
    } else if (dropdownItem.value === 'export') {
      navigator.clipboard.writeText(value.join('\n')).then(() => {
        MessagePlugin.success('已复制到剪贴板');
      })
        .catch((err) => {
          console.log(err);
        });
    } else if (dropdownItem.value === 'clear') {
      onChange(['']);
    }
  }, [value, onChange]);

  const handleConfirm = useCallback(() => {
    form.submit();
  }, [form]);

  const handleClose = useCallback(() => {
    setVisible(false);
  }, []);

  const handleSubmit = useCallback(() => {
    const val = form.getFieldsValue(true)?.text || '';
    onChange([...value, ...val.split('\n').filter((item: string) => item.trim().length > 0)]);
    setVisible(false);
    form.reset();
  }, [onChange, value]);

  useEffect(() => {
    if (!isEmpty(value)) {
      setCompValue([...value]);
      setArrayKeys();
      ref.current = [...value];
      form.setFieldsValue(value);
    }
  }, [value]);

  const setArrayKeys = () => {
    if (keys?.length !== value?.length) {
      const newKeys = [...value]?.map((_e, i) => {
        if (i === changeKeyIndex) {
          return `${random()}_${i}`;
        }
        return tempKeys?.[i] || `${random()}_${i}`;
      });
      setKeys(newKeys);
    }
  };
  const removeItem = (index: number, event: React.MouseEvent<HTMLElement, MouseEvent>) => {
    event.stopPropagation();
    const newArray = [...compValue];

    newArray.splice(index, 1);
    onChange([...newArray]);
    const tempKeys = [...keys];
    tempKeys.splice(index, 1);
    setKeys(tempKeys);
    if (newArray?.length === 0) {
      setCompValue([]);
    }
  };

  const addItem = (index: number, event: React.MouseEvent<HTMLElement, MouseEvent>) => {
    event.stopPropagation();
    const aArray = [...compValue];
    aArray.splice(index + 1, 0, ...defaultValue);
    onChange(aArray);

    const tempKeys = [...keys];
    tempKeys.splice(index + 1, 0, random());
    setChangeKeyIndex(index + 1);
    setTempKeys(tempKeys);
  };

  const onEnd = (evt: Sortable.SortableEvent) => {
    const oldIndex = evt.oldIndex || 0;
    const newIndex = evt.newIndex || 0;
    sortKey(oldIndex, newIndex);
    sortData(oldIndex, newIndex);
  };

  const sortData = (oldIndex: number, newIndex: number) => {
    const copyValue = [...compValue];
    const copyValue2 = [...compValue];
    copyValue.splice(oldIndex, 1);
    copyValue.splice(newIndex, 0, copyValue2[oldIndex]);
    onChange(copyValue);
  };

  const sortKey = (oldIndex: number, newIndex: number) => {
    const copyValue = [...keys];
    const copyValue2 = [...keys];
    copyValue.splice(oldIndex, 1);
    copyValue.splice(newIndex, 0, copyValue2[oldIndex]);
    setKeys(copyValue);
  };

  const changeArrayInfoObject = (key: number, value: { [key: string]: unknown }) => {
    const temp = [...ref.current];
    temp[key] = value;
    onChange([...temp]);
  };

  const handleChange = useCallback((value: string, index: number) => {
    changeArrayInfoObject(index, value as any);
  }, []);
  return (
    <div className={styles.root}>
      {hasOperation && <div className={styles.operations}>
        <Dropdown options={options} onClick={clickHandler}>
          <Button variant="text" style={{ padding: 0 }} disabled={disabled}
            size='small' icon={<More />}></Button>
        </Dropdown>
      </div>}
      <div className={styles.arrayWrap}>
        <ReactSortable
          disabled={!drag || disabled}
          handle={'.draggable'}
          list={compValue}
          onEnd={onEnd}
          setList={() => { }}
        >
          {compValue.map(({ }, index: number) => (
            <div className="custom-form-group" key={`${keys?.[index]}`}>
              {drag && <Button
                className={classNames('draggable', styles.drag)}
                theme="default"
                style={{ padding: 0, background: 'transparent', border: 0, marginRight: 10 }}
                icon={<DragIcon />}
              ></Button>}
              <div style={{ flexGrow: '1', width: '100%' }}>
                <ItemComp
                  value={ref.current[index]}
                  disabled={disabled}
                  onChange={(value) => {
                    handleChange(value, index);
                  }} />
              </div>
              <FormItem className={styles.options}>
                <Button
                  className={styles.delete}
                  theme="default"
                  disabled={compValue.length === 1 || disabled}
                  size={buttonSize !== 'small' ? 'medium' : 'small'}
                  onClick={event => removeItem(index, event)}
                  icon={<DeleteIcon />}
                  style={{ padding: buttonSize === 'small' ? '0 9px' : undefined }}
                >{buttonSize === 'normal' && '删除'}</Button>
                <Button
                  className={styles.add}
                  theme="default"
                  disabled={disabled}
                  size={buttonSize !== 'small' ? 'medium' : 'small'}
                  onClick={event => addItem(index, event)}
                  icon={<AddIcon />}
                  style={{ padding: buttonSize === 'small' ? '0 8px' : undefined }}
                >{buttonSize === 'normal' && '增加'}</Button>
              </FormItem>
            </div>
          ))}
        </ReactSortable>
      </div>
      <Dialog visible={visible} header='文本解析' onClose={handleClose} onConfirm={handleConfirm}>
        <Form form={form} style={{ marginTop: 12 }} onSubmit={handleSubmit}>
          <FormItem name='text'>
            <Textarea rows={5} />
          </FormItem>
        </Form>
      </Dialog>
    </div>
  );
};
